import { Box, Button, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Alert, Color } from '@material-ui/lab';
import { AlertProps, Risk } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';

type OrderInsightAlertProps = {
  alerts?: Risk[];
  warnings?: Risk[];
  alertWithContent?: AlertProps[];
  unknowns?: Risk[];
  errors?: Risk[];
  errorMessages?: string[];
  hasErrorMessagesHtml?: boolean;
  isPdf?: boolean;
  className?: string;
};

// Uses the first link for Learn More and preserves any remaining links in the description.
const extractLink = (
  htmlString?: string,
): { text: string; href: string | null; cleanText: string; hasHtml: boolean } => {
  if (!htmlString) {
    return { text: '', href: null, cleanText: '', hasHtml: false };
  }

  const linkRegex = /<a\s+[^>]*?href=['"]([^'"]*)['"][^>]*>(.*?)<\/a>/i;
  const match = linkRegex.exec(htmlString);

  if (match) {
    const href = match[1];
    const linkText = match[2];
    const cleanText = htmlString.replace(match[0], linkText).trim();
    const hasHtml = /<a\s/i.test(cleanText);
    return { text: linkText, href, cleanText, hasHtml };
  }

  return { text: '', href: null, cleanText: htmlString, hasHtml: false };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Alert icon and action positioning (applies to all alerts)
    alert: {
      position: 'relative',
      '& .MuiAlert-icon': {
        marginRight: theme.spacing(1.5),
        padding: `${theme.spacing(0.625)} 0`,
        '& svg': {
          width: '24px',
          height: '24px',
        },
        '& .iconContainer': {
          alignItems: 'flex-start',
        },
      },
      '& .MuiAlert-action': {
        position: 'absolute',
        right: theme.spacing(2),
        top: theme.spacing(0.625),
        padding: 0,
        marginRight: 0,
        marginLeft: 0,
      },
    },
    alertWithAction: {
      '& .MuiAlert-message': {
        paddingRight: theme.spacing(12),
      },
    },
    // PDF-specific overrides
    pdfAlert: {
      padding: theme.spacing(0, 2),
      maxWidth: '500px',
      '& .MuiAlert-icon': {
        marginLeft: 0,
        marginRight: 0,
        padding: `${theme.spacing(0.625)} 0`,
        '& svg': {
          width: '12px',
          height: '22px',
        },
      },
    },
    // Override for secondary (unknown/error) alerts
    secondary: {
      background: theme.palette.grey[100],
      color: theme.palette.text.secondary,
      borderColor: theme.palette.grey[300],
    },
    grey: {
      color: theme.palette.text.secondary,
      margin: '0',
    },
    mt0: {
      margin: '0',
    },
    description: {
      margin: '0',
      '& a': {
        color: 'inherit',
        textDecoration: 'underline',
      },
    },
    learnMoreButton: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '14px',
      letterSpacing: '0.46px',
      padding: theme.spacing(0.125, 0.625),
      minWidth: 'auto',
      color: 'inherit',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

const OrderInsightAlert: FC<OrderInsightAlertProps> = ({
  alerts = [],
  warnings = [],
  alertWithContent = [],
  unknowns = [],
  errors = [],
  errorMessages = [],
  hasErrorMessagesHtml = false,
  isPdf,
  className = '',
}) => {
  const classes = useStyles();

  const renderRiskAlert = (risk: Risk, index: number, type: 'warning' | 'error' | 'unknown' | 'alert') => {
    const severityMap = {
      warning: 'warning',
      alert: 'error',
      unknown: undefined,
      error: undefined,
    };

    const classMap = {
      warning: undefined, // Use theme styling
      alert: undefined, // Use theme styling
      unknown: classes.secondary,
      error: classes.secondary,
    };

    const alertSeverity = severityMap[type] as Color | undefined;

    // Extract first link for Learn More; keep any additional links in the description
    const { href, cleanText, hasHtml } = extractLink(risk.description);

    // In PDF context, use anchor tag instead of button with onClick
    const actionButton: ReactNode = href ? (
      isPdf ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.learnMoreButton}
          style={{ textDecoration: 'none', display: 'inline-block' }}
        >
          Learn More
        </a>
      ) : (
        <Button
          variant="text"
          className={classes.learnMoreButton}
          size="small"
          onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
          disableRipple
        >
          Learn More
        </Button>
      )
    ) : undefined;

    return (
      <Alert
        {...(alertSeverity && { severity: alertSeverity })}
        icon={<OrderInsightsRiskIcon isPdf={isPdf} riskSeverity={risk.severityName} />}
        className={clsx(
          classes.alert,
          isPdf && classes.pdfAlert,
          actionButton && classes.alertWithAction,
          classMap[type],
        )}
        key={index}
        action={actionButton}
      >
        {type !== 'unknown' && <strong className={classes.mt0}>{risk.title}</strong>}
        {hasHtml ? (
          <p
            className={clsx(classes.description, type === 'unknown' && classes.grey)}
            dangerouslySetInnerHTML={{ __html: cleanText }}
          />
        ) : (
          <p className={clsx(classes.description, type === 'unknown' && classes.grey)}>{cleanText}</p>
        )}
      </Alert>
    );
  };

  return (
    <Box className={className}>
      {errorMessages.length > 0 && (
        <Alert
          severity="error"
          icon={<OrderInsightsRiskIcon isPdf={isPdf} riskSeverity="Alert" />}
          className={clsx(classes.alert, isPdf && classes.pdfAlert)}
        >
          <div>
            {errorMessages.map((risk: string, index: number) => {
              if (!hasErrorMessagesHtml) {
                const [title, ...descriptionParts] = risk.split(':');
                const description = descriptionParts.join(':').trim();
                return (
                  <div key={index}>
                    <strong className={classes.mt0}>{title}:</strong> {description}
                  </div>
                );
              }

              return (
                <div key={index}>
                  <span dangerouslySetInnerHTML={{ __html: risk }} />
                </div>
              );
            })}
          </div>
        </Alert>
      )}

      {alerts.length > 0 && alerts.map((risk, index) => renderRiskAlert(risk, index, 'alert'))}
      {warnings.length > 0 && warnings.map((risk, index) => renderRiskAlert(risk, index, 'warning'))}
      {alertWithContent.length > 0 && (
        <>
          {alertWithContent.map((alertData: AlertProps, index: number) => (
            <Alert
              severity={alertData.severity}
              className={clsx(classes.alert, isPdf && classes.pdfAlert, alertData.action && classes.alertWithAction)}
              key={index}
              {...(alertData.action ? { action: alertData.action } : {})}
              {...(alertData.icon ? { icon: alertData.icon } : {})}
            >
              {alertData.content}
            </Alert>
          ))}
        </>
      )}
      {unknowns.length > 0 && unknowns.map((risk, index) => renderRiskAlert(risk, index, 'unknown'))}
      {errors.length > 0 && errors.map((risk, index) => renderRiskAlert(risk, index, 'error'))}
    </Box>
  );
};

export default OrderInsightAlert;
